/* eslint-disable unicorn/filename-case, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { TabKeywords } from '../-tab-keywords';
import { BlymLoading } from '../../../../components/loading';
import { ToastVariant } from '../../../../components/toast';
import type { SearchSeoStrategyKeywordsReq } from '../../../../entity/api/routes/seo-strategy';
import type { SeoStrategyKeyword } from '../../../../entity/api/seo-strategy-keywords';
import { addTrackCrumb, trackError } from '../../../../error/tracking';
import { captureException } from '../../../../logger/capture-exception';
import {
  useLinkSeoStrategyKeyword,
  useRemoveSeoStrategyKeyword,
  useSeoStrategyKeywords,
} from '../../../../services/storage/seo-strategy-queries';
import { tNamespace } from '../../../../services/translation';
import { useToast } from '../../../../shared/contexts/toast-context';
import { useDebouncedEffect } from '../../../../shared/hooks/use-debounce';
import { useContentModals } from '../../_content-modals-context';

const t = tNamespace('app.seo-strategy-keywords');

const KEYWORDS_PER_PAGE = 20;

type KeywordsSearch = {
  page?: number;
  search?: string;
  difficultyFilter?: 'all' | 'easy' | 'medium' | 'hard';
  positionFilter?: 'all' | 'top3' | 'top10' | 'top30' | 'notranking';
  funnelFilter?: 'all' | 'bofu' | 'mofu' | 'tofu';
  sortBy?: string;
  sortByDir?: 'ASC' | 'DESC';
};

export const Route = createFileRoute('/app/_sidebar-layout/seo-strategy/$brandId/keywords')({
  validateSearch: function (search: Record<string, unknown>): KeywordsSearch {
    let validatedPage = 1;
    if (typeof search.page === 'number') {
      validatedPage = Math.max(Math.floor(search.page), 1);
    }

    const difficultyOptions = ['all', 'easy', 'medium', 'hard'] as const;
    const positionOptions = ['all', 'top3', 'top10', 'top30', 'notranking'] as const;
    const funnelOptions = ['all', 'bofu', 'mofu', 'tofu'] as const;

    return {
      page: validatedPage,
      search: typeof search.search === 'string' ? search.search : undefined,
      difficultyFilter: difficultyOptions.includes(
        search.difficultyFilter as (typeof difficultyOptions)[number]
      )
        ? (search.difficultyFilter as KeywordsSearch['difficultyFilter'])
        : undefined,
      positionFilter: positionOptions.includes(
        search.positionFilter as (typeof positionOptions)[number]
      )
        ? (search.positionFilter as KeywordsSearch['positionFilter'])
        : undefined,
      funnelFilter: funnelOptions.includes(search.funnelFilter as (typeof funnelOptions)[number])
        ? (search.funnelFilter as KeywordsSearch['funnelFilter'])
        : undefined,
      sortBy: typeof search.sortBy === 'string' ? search.sortBy : 'finalScore',
      sortByDir:
        search.sortByDir === 'ASC' || search.sortByDir === 'DESC' ? search.sortByDir : 'DESC',
    };
  },

  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',
      message: '/app/_sidebar-layout/seo-strategy/$brandId/keywords',
      level: 'info',
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: SeoStrategyKeywordsTab,
});

function SeoStrategyKeywordsTab() {
  const { brandId } = Route.useParams();
  const navigate = useNavigate();
  const urlSearch = Route.useSearch();
  const { openGenerationModal, openRewriteModal } = useContentModals();
  const addToast = useToast();
  // URL is the source of truth
  const page = urlSearch.page ?? 1;
  const searchTerm = urlSearch.search ?? '';
  const difficultyFilter = urlSearch.difficultyFilter ?? 'all';
  const positionFilter = urlSearch.positionFilter ?? 'all';
  const funnelFilter = urlSearch.funnelFilter ?? 'all';
  const sortBy = urlSearch.sortBy ?? 'finalScore';
  const sortByDir = urlSearch.sortByDir ?? 'DESC';

  // Local state only for the search input (debounced before pushing to URL)
  const [searchInput, setSearchInput] = useState(searchTerm);

  const query = useMemo(
    function (): SearchSeoStrategyKeywordsReq['query'] {
      const params: NonNullable<SearchSeoStrategyKeywordsReq['query']> = {
        limit: KEYWORDS_PER_PAGE,
        offset: (page - 1) * KEYWORDS_PER_PAGE,
      };
      if (searchTerm.trim().length > 0) {
        params.search = searchTerm.trim();
      }
      switch (difficultyFilter) {
        case 'easy': {
          params.difficultyMax = 20;
          break;
        }
        case 'medium': {
          params.difficultyMin = 21;
          params.difficultyMax = 50;
          break;
        }
        case 'hard': {
          params.difficultyMin = 51;
          break;
        }
      }
      switch (positionFilter) {
        case 'top3': {
          params.positionMin = 1;
          params.positionMax = 3;
          break;
        }
        case 'top10': {
          params.positionMin = 1;
          params.positionMax = 10;
          break;
        }
        case 'top30': {
          params.positionMin = 1;
          params.positionMax = 30;
          break;
        }
        case 'notranking': {
          params.positionMax = 0;
          break;
        }
      }
      if (funnelFilter !== 'all') {
        params.funnel = funnelFilter;
      }
      params.sortBy = sortBy;
      params.sortByDir = sortByDir;
      return params;
    },
    [searchTerm, page, difficultyFilter, positionFilter, funnelFilter, sortBy, sortByDir]
  );

  const { data, isPending, isLoading } = useSeoStrategyKeywords(brandId, query);
  const removeMutation = useRemoveSeoStrategyKeyword();
  const linkMutation = useLinkSeoStrategyKeyword();
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil((data?.totalCount ?? 0) / KEYWORDS_PER_PAGE));

  // Keep input in sync with back/forward navigation
  useEffect(
    function () {
      setSearchInput(searchTerm);
    },
    [searchTerm]
  );

  // Debounce search input -> push to URL
  useDebouncedEffect(
    function () {
      if (searchInput === searchTerm) {
        return;
      }
      navigate({
        from: Route.fullPath,
        search: function (prev) {
          return {
            ...prev,
            search: searchInput.trim().length > 0 ? searchInput.trim() : undefined,
            page: 1,
          };
        },
      }).catch(function (err) {
        captureException(
          err,
          {
            file: 'seo-strategy/$brandId/keywords.tsx',
            method: 'useDebouncedEffect[searchInput]',
            infoMessage: 'Failed to update search in URL',
          },
          trackError
        );
      });
    },
    [searchInput, searchTerm, navigate],
    500
  );

  // Redirect to valid page if out of bounds
  useEffect(
    function () {
      if (isLoading) {
        return;
      }
      if (page < 1 || page > totalPages) {
        navigate({
          from: Route.fullPath,
          search: function (prev) {
            return {
              ...prev,
              page: Math.min(Math.max(page, 1), totalPages),
            };
          },
          replace: true,
        }).catch(function (err) {
          captureException(
            err,
            {
              file: 'seo-strategy/$brandId/keywords.tsx',
              method: 'useEffect[pageValidation]',
              infoMessage: 'Failed to redirect to valid page',
            },
            trackError
          );
        });
      }
    },
    [page, totalPages, isLoading, navigate]
  );

  function handleSearchChange(value: string) {
    setSearchInput(value);
  }

  function handleFunnelFilterChange(value: string) {
    navigate({
      from: Route.fullPath,
      search: function (prev) {
        return {
          ...prev,
          funnelFilter: value === 'all' ? undefined : (value as KeywordsSearch['funnelFilter']),
          page: 1,
        };
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'seo-strategy/$brandId/keywords.tsx',
          method: 'handleFunnelFilterChange',
          infoMessage: 'Failed to update funnelFilter in URL',
        },
        trackError
      );
    });
  }

  function handleDifficultyFilterChange(value: string) {
    navigate({
      from: Route.fullPath,
      search: function (prev) {
        return {
          ...prev,
          difficultyFilter:
            value === 'all' ? undefined : (value as KeywordsSearch['difficultyFilter']),
          page: 1,
        };
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'seo-strategy/$brandId/keywords.tsx',
          method: 'handleDifficultyFilterChange',
          infoMessage: 'Failed to update difficultyFilter in URL',
        },
        trackError
      );
    });
  }

  function handlePositionFilterChange(value: string) {
    navigate({
      from: Route.fullPath,
      search: function (prev) {
        return {
          ...prev,
          positionFilter: value === 'all' ? undefined : (value as KeywordsSearch['positionFilter']),
          page: 1,
        };
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'seo-strategy/$brandId/keywords.tsx',
          method: 'handlePositionFilterChange',
          infoMessage: 'Failed to update positionFilter in URL',
        },
        trackError
      );
    });
  }

  function handleSortChange(key: string, dir: 'ASC' | 'DESC') {
    navigate({
      from: Route.fullPath,
      search: function (prev) {
        return { ...prev, sortBy: key, sortByDir: dir, page: 1 };
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'seo-strategy/$brandId/keywords.tsx',
          method: 'handleSortChange',
          infoMessage: 'Failed to update sort in URL',
        },
        trackError
      );
    });
  }

  function handlePageChange(newPage: number) {
    navigate({
      from: Route.fullPath,
      search: function (prev) {
        return { ...prev, page: newPage };
      },
    }).catch(function (err) {
      captureException(
        err,
        {
          file: 'seo-strategy/$brandId/keywords.tsx',
          method: 'handlePageChange',
          infoMessage: 'Failed to navigate to page',
        },
        trackError
      );
    });
  }

  function handleActionClick(kw: SeoStrategyKeyword, action: 'create' | 'refresh') {
    if (actionLoadingId != null) {
      return;
    }
    if (action === 'refresh' && kw.url.length === 0) {
      addToast({
        title: t('noUrlForRefresh'),
        variant: ToastVariant.Error,
        id: Date.now(),
      });
      return;
    }
    const hasUrl = kw.url.length > 0;

    setActionLoadingId(kw._id);
    linkMutation
      .mutateAsync({
        brandId,
        data: { keyword: kw.name, keywordId: kw._id },
      })
      .then(function ({ result }) {
        if (result.linked) {
          addToast({
            title: t('keywordLinked'),
            variant: ToastVariant.Success,
            id: Date.now(),
          });
        } else {
          if (action === 'refresh' && hasUrl) {
            openRewriteModal({ url: kw.url });
          } else {
            openGenerationModal({ keyword: kw.name });
          }
        }
      })
      .catch(function (err) {
        captureException(
          err,
          {
            file: 'seo-strategy/$brandId/keywords.tsx',
            method: 'handleActionClick',
            infoMessage: 'Link keyword to content failed (non-blocking)',
          },
          trackError
        );
      })
      .finally(function () {
        setActionLoadingId(null);
      });
  }

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <BlymLoading />
      </div>
    );
  }

  function handleRemoveMultiple(keywordIds: string[]) {
    for (const keywordId of keywordIds) {
      removeMutation.mutate({ brandId, keywordId });
    }
  }

  return (
    <div className="w-full pb-12 pt-0 md:pb-6">
      <TabKeywords
        keywords={data?.data ?? []}
        totalCount={data?.totalCount ?? 0}
        onRemove={function (keywordId) {
          removeMutation.mutate({ brandId, keywordId });
        }}
        onRemoveMultiple={handleRemoveMultiple}
        onActionClick={handleActionClick}
        onOpenContent={function (contentId) {
          navigate({ to: '/app/content/$id', params: { id: contentId } }).catch(function (err) {
            captureException(
              err,
              {
                file: 'seo-strategy/$brandId/keywords.tsx',
                method: 'onOpenContent',
                infoMessage: 'Failed to navigate to content',
              },
              trackError
            );
          });
        }}
        actionLoadingId={actionLoadingId}
        search={searchInput}
        onSearchChange={handleSearchChange}
        funnelFilter={funnelFilter}
        onFunnelFilterChange={handleFunnelFilterChange}
        difficultyFilter={difficultyFilter}
        onDifficultyFilterChange={handleDifficultyFilterChange}
        positionFilter={positionFilter}
        onPositionFilterChange={handlePositionFilterChange}
        page={page}
        onPageChange={handlePageChange}
        totalPages={totalPages}
        sortBy={sortBy}
        sortByDir={sortByDir}
        onSort={handleSortChange}
      />
    </div>
  );
}
